import { useSeoMeta } from '@unhead/react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ZapPollCard, ZapPollCardSkeleton } from '@/components/ZapPollCard';
import { LoginArea } from '@/components/auth/LoginArea';
import { usePolls } from '@/hooks/usePolls';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Vote as VoteIcon, Zap, ArrowLeft, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUND2_EVENT_ID, ROUND2_IMAGE_URL } from '@/lib/constants';

export default function VotePage() {
  const { data: polls, isLoading, isError } = usePolls(ROUND2_EVENT_ID);
  const { user } = useCurrentUser();

  useSeoMeta({
    title: 'Round 2 - Bitcoin Maxi Madness',
    description: 'Round 2 is live! Cast your votes in the Bitcoin Maxi Madness bracket challenge. Powered by Nostr zap polls.',
  });

  const activePolls = polls?.filter(p => !p.closedAt || (Date.now() / 1000) <= p.closedAt) ?? [];
  const closedPolls = polls?.filter(p => p.closedAt && (Date.now() / 1000) > p.closedAt) ?? [];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main>
        {/* Page hero */}
        <section className="relative py-10 sm:py-14 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(247,147,26,0.08)_0%,transparent_60%)]" />

          <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge className="bg-primary/10 text-primary border border-primary/20 text-xs px-3 py-1 tracking-wider uppercase font-medium">
                  <Trophy className="w-3 h-3 mr-1.5" />
                  Round 2 — Active
                </Badge>
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                Round 2: <span className="text-bitcoin-gradient">Vote Now</span>
              </h1>

              <p className="text-muted-foreground text-sm sm:text-base max-w-xl leading-relaxed">
                Round 1 is in the books. The survivors advance! Vote by zapping your pick with sats.
                Minimum {polls?.[0]?.valueMinimum ?? 21} sats per vote.
              </p>

              {/* Round 2 bracket image */}
              <img
                src={ROUND2_IMAGE_URL}
                alt="Round 2 Bracket"
                className="w-full rounded-xl border border-border/30 shadow-lg"
                loading="eager"
              />

              {!user && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-primary/5 border border-primary/10">
                  <Zap className="w-5 h-5 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground/80">
                      Sign in with your Nostr key to vote in every matchup.
                    </p>
                  </div>
                  <LoginArea className="flex-shrink-0 max-w-48" />
                </div>
              )}

              {/* Round 1 link */}
              <Link
                to="/round1"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                View Round 1 Results →
              </Link>
            </div>
          </div>
        </section>

        {/* Polls list */}
        <section className="relative pb-16 sm:pb-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            {isLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <ZapPollCardSkeleton key={i} />
                ))}
              </div>
            ) : isError ? (
              <Card className="border-destructive/20 bg-destructive/5">
                <CardContent className="py-12 px-8 text-center">
                  <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    Failed to load polls. Please check your relay connections and try again.
                  </p>
                </CardContent>
              </Card>
            ) : polls && polls.length > 0 ? (
              <div className="space-y-8">
                {activePolls.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                      <h2 className="text-sm font-semibold uppercase tracking-wider text-primary">
                        Active Matchups ({activePolls.length})
                      </h2>
                    </div>
                    {activePolls.map((poll, i) => (
                      <ZapPollCard key={poll.event.id} poll={poll} index={i} />
                    ))}
                  </div>
                )}

                {closedPolls.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-muted-foreground/40" />
                      <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                        Completed Matchups ({closedPolls.length})
                      </h2>
                    </div>
                    {closedPolls.map((poll, i) => (
                      <ZapPollCard key={poll.event.id} poll={poll} index={activePolls.length + i} />
                    ))}
                  </div>
                )}

                <p className="text-center text-xs text-muted-foreground/50 pt-4">
                  {polls.length} Round 2 matchup{polls.length !== 1 ? 's' : ''} found
                </p>
              </div>
            ) : (
              <Card className="border-dashed border-border/50 bg-card/50">
                <CardContent className="py-16 px-8 text-center">
                  <div className="max-w-sm mx-auto space-y-4">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                      <VoteIcon className="w-8 h-8 text-primary/60" />
                    </div>
                    <p className="text-muted-foreground text-sm">
                      Round 2 polls will appear here as Bugle.News publishes them. Check back soon!
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
